"use client";

import { useState, useCallback, useEffect, useMemo, Suspense, type ReactNode } from "react";

import { useSearchParams } from "next/navigation";

import { MapProvider } from "react-map-gl/maplibre";

import { AddressSearchInput } from "@/components/find-my-school/address-search-input";
import { shouldShowAddressNoResults } from "@/components/find-my-school/address-search-state";
import { CategoryTabs } from "@/components/find-my-school/category-tabs";
import { CommunityServicesList } from "@/components/find-my-school/community-services-list";
import { FindMySchoolHeader } from "@/components/find-my-school/find-my-school-header";
import { FindMySchoolLayersProvider } from "@/components/find-my-school/find-my-school-layers-context";
import { FindMySchoolMap } from "@/components/find-my-school/find-my-school-map";
import {
  FindMySchoolMobileDrawer,
  type MobileDrawerMetrics,
  type MobileDrawerSnap,
} from "@/components/find-my-school/find-my-school-mobile-drawer";
import {
  FindMySchoolModeTabs,
  type FindMySchoolSidebarMode,
} from "@/components/find-my-school/find-my-school-mode-tabs";
import { SchoolList } from "@/components/find-my-school/school-list";
import { CancelSmallIcon, PinAltIcon } from "@/components/icons-lib";
import { useUserLocation } from "@/hooks/find-my-school/use-user-location";
import { MapStyleProvider } from "@/providers/maps/contexts/map-style-context";
import findMySchoolApi, {
  useValidateDistrictQuery,
  useGeocodeAddressPublicQuery,
  useLazyGetFindMySchoolSchoolsQuery,
  useLazyGetFindMySchoolMapLevelBoundariesQuery,
} from "@/providers/store/api/find-my-school/find-my-school";
import type {
  SchoolType,
  IDistrictValidationResponse,
  IGeocodeAddressPublicItem,
  IFindMySchoolMapLevelBoundaryItem,
  IFindMySchoolSchoolItem,
} from "@/providers/store/api/find-my-school/find-my-school.interface";
import { useAppDispatch } from "@/providers/store/hooks";
import { cn } from "@/shared/helpers/cn.helper";
import { toOptionalDistrictBbox } from "@/shared/utils/district-geocode-bbox.util";
import {
  buildCommunityQueryArgs,
  buildSchoolsQueryArgs,
  communityResultsMatchRequest,
  getCommittedAddress,
  getDesktopSidebarTitle,
  getDisplayedSchoolItems,
  getDistrictCenter,
  getHighlightedBoundaryBbox,
  resolveNextHighlightedSchoolId,
  schoolResultsMatchRequest,
} from "@/shared/utils/find-my-school-page.util";

const MOBILE_VIEWPORT_QUERY = "(max-width: 767px)";
const EMPTY_SCHOOL_ITEMS: IFindMySchoolSchoolItem[] = [];
const EMPTY_COMMUNITY_ITEMS: IFindMySchoolMapLevelBoundaryItem[] = [];
const DEFAULT_MOBILE_DRAWER_METRICS: MobileDrawerMetrics = {
  height: 150,
  controlsOpacity: 1,
  mapBottomPadding: 150,
};

function useMobileViewport(): boolean | null {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateViewport = () => setIsMobile(mediaQuery.matches);
    updateViewport();
    mediaQuery.addEventListener("change", updateViewport);
    return () => mediaQuery.removeEventListener("change", updateViewport);
  }, []);

  return isMobile;
}

function LoadingScreen() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
    </div>
  );
}

function AddressCardRow({
  address,
  onClear,
}: {
  address: string;
  onClear: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-[12px] bg-[#F3F3F3] px-3 py-2 text-[14px]/[20px] font-normal text-[#161515]">
      <span className="min-w-0 flex-1 break-words">{address}</span>
      <button
        aria-label="Clear address"
        className="shrink-0 rounded p-0.5 hover:bg-black/5"
        type="button"
        onClick={onClear}
      >
        <CancelSmallIcon className="h-4 w-4 text-[#161515]" />
      </button>
    </div>
  );
}

interface FindMySchoolLayoutSharedProps {
  districtData: IDistrictValidationResponse;
  addressInput: string;
  dropPinMode: boolean;
  isGeocoding: boolean;
  showNoAddressResults: boolean;
  visibleSuggestions: IGeocodeAddressPublicItem[];
  mapContent: ReactNode;
  onAddressInputChange: (value: string) => void;
  onClearAddress: () => void;
  onAddressSearch: (address: string) => void;
  onAddressSelect: (item: IGeocodeAddressPublicItem) => void;
}

interface FindMySchoolMobileLayoutProps extends FindMySchoolLayoutSharedProps {
  committedAddress: string | null;
  communityItems: IFindMySchoolMapLevelBoundaryItem[];
  hasAddress: boolean;
  hasAddressResults: boolean;
  highlightedBoundaryId: string | null;
  highlightedSchoolId: string | null;
  isCommunityListLoading: boolean;
  isSchoolListLoading: boolean;
  outsideDistrictBoundary: boolean;
  displayedSchoolItems: IFindMySchoolSchoolItem[];
  selectedCategory: SchoolType;
  showCommunityMode: boolean;
  sidebarMode: FindMySchoolSidebarMode;
  mobileDrawerSnap: MobileDrawerSnap;
  onCategoryChange: (category: SchoolType) => void;
  onCommunityItemClick: (item: IFindMySchoolMapLevelBoundaryItem) => void;
  onMobileDrawerMetricsChange: (metrics: MobileDrawerMetrics) => void;
  onSchoolCardClick: (school: IFindMySchoolSchoolItem) => void;
  onSidebarModeChange: (mode: FindMySchoolSidebarMode) => void;
  onMobileDrawerSnapChange: (snap: MobileDrawerSnap) => void;
  onMobileDropPinClick: () => void;
}

function FindMySchoolMobileLayout({
  districtData,
  addressInput,
  dropPinMode,
  isGeocoding,
  showNoAddressResults,
  visibleSuggestions,
  mapContent,
  onAddressInputChange,
  onClearAddress,
  onAddressSearch,
  onAddressSelect,
  committedAddress,
  communityItems,
  hasAddress,
  hasAddressResults,
  highlightedBoundaryId,
  highlightedSchoolId,
  isCommunityListLoading,
  isSchoolListLoading,
  outsideDistrictBoundary,
  displayedSchoolItems,
  selectedCategory,
  showCommunityMode,
  sidebarMode,
  mobileDrawerSnap,
  onCategoryChange,
  onCommunityItemClick,
  onMobileDrawerMetricsChange,
  onSchoolCardClick,
  onSidebarModeChange,
  onMobileDrawerSnapChange,
  onMobileDropPinClick,
}: FindMySchoolMobileLayoutProps) {
  return (
    <div className="flex h-[100dvh] min-h-[560px] w-full flex-col overflow-hidden bg-background-primary">
      <FindMySchoolHeader
        mobile
        districtName={districtData.name}
        dropPinMode={dropPinMode}
        isLoading={isGeocoding}
        placeholder="Enter your address..."
        showNoResults={showNoAddressResults}
        suggestions={visibleSuggestions}
        value={addressInput}
        onChange={onAddressInputChange}
        onClear={onClearAddress}
        onDropPinClick={onMobileDropPinClick}
        onSearch={onAddressSearch}
        onSelect={onAddressSelect}
      />

      <div className="relative min-h-0 flex-1 overflow-hidden bg-[#EAE7E1]">
        {mapContent}

        <div className="absolute left-3 right-3 top-3 z-40 flex items-start gap-2">
          <AddressSearchInput
            className="min-w-0 flex-1"
            isLoading={isGeocoding}
            placeholder="Enter your address..."
            showNoResults={showNoAddressResults}
            suggestions={visibleSuggestions}
            value={addressInput}
            variant="mobile"
            onChange={onAddressInputChange}
            onClear={onClearAddress}
            onSearch={onAddressSearch}
            onSelect={onAddressSelect}
          />
          <button
            aria-label={dropPinMode ? "Cancel dropping pin" : "Drop pin on map"}
            aria-pressed={dropPinMode}
            className={cn(
              "flex h-12 shrink-0 items-center gap-1 rounded-full border px-3 shadow-[0_2px_10px_rgba(0,0,0,0.20)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#520313]",
              dropPinMode
                ? "border-[#520313] bg-[#520313] text-white"
                : "border-[#E5E4E3] bg-white text-[#520313]",
            )}
            title={dropPinMode ? "Cancel dropping pin" : "Drop pin on map"}
            type="button"
            onClick={onMobileDropPinClick}
          >
            <PinAltIcon className="h-5 w-5" />
            <span className="text-[12px]/[16px] font-semibold">
              {dropPinMode ? "Cancel" : "Drop pin"}
            </span>
          </button>
        </div>

        <FindMySchoolMobileDrawer
          address={committedAddress}
          communityItems={communityItems}
          hasAddress={hasAddress}
          hasSearchedAddress={hasAddressResults}
          highlightedBoundaryId={highlightedBoundaryId}
          highlightedSchoolId={highlightedSchoolId}
          isCommunityLoading={isCommunityListLoading}
          isLoading={isSchoolListLoading}
          outsideDistrictBoundary={outsideDistrictBoundary}
          schoolLevelTypes={districtData.schoolLevelTypes ?? []}
          schools={displayedSchoolItems}
          selectedCategory={selectedCategory}
          showCommunityMode={showCommunityMode}
          sidebarMode={sidebarMode}
          snap={mobileDrawerSnap}
          onCategoryChange={onCategoryChange}
          onClearAddress={onClearAddress}
          onCommunityItemClick={onCommunityItemClick}
          onMetricsChange={onMobileDrawerMetricsChange}
          onSchoolClick={onSchoolCardClick}
          onSidebarModeChange={onSidebarModeChange}
          onSnapChange={onMobileDrawerSnapChange}
        />
      </div>
    </div>
  );
}

interface FindMySchoolDesktopLayoutProps extends FindMySchoolLayoutSharedProps {
  hasSearchedAddress: boolean;
  searchedAddress: string | null;
  showCommunityMode: boolean;
  sidebarMode: FindMySchoolSidebarMode;
  selectedCategory: SchoolType;
  highlightedSchoolId: string | null;
  isSchoolsLoading: boolean;
  outsideDistrictBoundary: boolean;
  displayedSchoolItems: IFindMySchoolSchoolItem[];
  communityItems: IFindMySchoolMapLevelBoundaryItem[];
  highlightedBoundaryId: string | null;
  isCommunityListLoading: boolean;
  hasUserLocation: boolean;
  onSidebarModeChange: (mode: FindMySchoolSidebarMode) => void;
  onCategoryChange: (category: SchoolType) => void;
  onSchoolCardClick: (school: IFindMySchoolSchoolItem) => void;
  onCommunityItemClick: (item: IFindMySchoolMapLevelBoundaryItem) => void;
  onDropPinClick: () => void;
}

function FindMySchoolDesktopLayout({
  districtData,
  addressInput,
  dropPinMode,
  isGeocoding,
  showNoAddressResults,
  visibleSuggestions,
  mapContent,
  onAddressInputChange,
  onClearAddress,
  onAddressSearch,
  onAddressSelect,
  hasSearchedAddress,
  searchedAddress,
  showCommunityMode,
  sidebarMode,
  selectedCategory,
  highlightedSchoolId,
  isSchoolsLoading,
  outsideDistrictBoundary,
  displayedSchoolItems,
  communityItems,
  highlightedBoundaryId,
  isCommunityListLoading,
  hasUserLocation,
  onSidebarModeChange,
  onCategoryChange,
  onSchoolCardClick,
  onCommunityItemClick,
  onDropPinClick,
}: FindMySchoolDesktopLayoutProps) {
  const showSchoolsPanel = sidebarMode === "schools";

  return (
    <div className="flex h-screen w-full flex-col">
      <FindMySchoolHeader
        districtName={districtData.name}
        dropPinMode={dropPinMode}
        isLoading={isGeocoding}
        placeholder="Enter your address..."
        showNoResults={showNoAddressResults}
        suggestions={visibleSuggestions}
        value={addressInput}
        onChange={onAddressInputChange}
        onClear={onClearAddress}
        onDropPinClick={onDropPinClick}
        onSearch={onAddressSearch}
        onSelect={onAddressSelect}
      />

      <div className="flex min-h-0 flex-1 gap-4 bg-background-primary p-4">
        <div className="flex w-1/3 min-w-0 flex-col rounded-[12px] border border-[#DFDFDF] bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.16)]">
          <div className="px-4 py-3">
            <div className="flex flex-col gap-2">
              <div className="text-[18px]/[28px] font-semibold text-[#161515]">
                {getDesktopSidebarTitle(sidebarMode, hasSearchedAddress)}
              </div>
              {hasSearchedAddress && searchedAddress && (
                <AddressCardRow
                  address={searchedAddress}
                  onClear={onClearAddress}
                />
              )}
            </div>
          </div>

          {showCommunityMode && (
            <div className="px-4 pb-3">
              <FindMySchoolModeTabs
                selectedMode={sidebarMode}
                onChange={onSidebarModeChange}
              />
            </div>
          )}

          {showSchoolsPanel ? (
            <>
              <div className="px-4 py-3">
                <CategoryTabs
                  schoolLevelTypes={districtData.schoolLevelTypes ?? []}
                  selectedCategory={selectedCategory}
                  onChange={onCategoryChange}
                />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
                <SchoolList
                  hasSearchedAddress={hasSearchedAddress}
                  highlightedSchoolId={highlightedSchoolId}
                  isLoading={isSchoolsLoading}
                  outsideDistrictBoundary={outsideDistrictBoundary}
                  schools={displayedSchoolItems}
                  onSchoolClick={onSchoolCardClick}
                />
              </div>
            </>
          ) : (
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              <CommunityServicesList
                hasSearchedAddress={hasUserLocation}
                highlightedBoundaryId={highlightedBoundaryId}
                isLoading={isCommunityListLoading}
                items={communityItems}
                onItemClick={onCommunityItemClick}
              />
            </div>
          )}
        </div>

        <div className="relative min-h-0 flex-1 overflow-hidden rounded-[12px] border border-[#DFDFDF] bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.16)]">
          {mapContent}
        </div>
      </div>
    </div>
  );
}

function FindMySchoolContent() {
  const searchParams = useSearchParams();
  const districtName = searchParams.get("district");

  if (!districtName) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">
            District Not Specified
          </h1>
          <p className="mt-2 text-gray-600">
            Please provide a district name in the URL:
            /find-my-school?district=Your+District
          </p>
        </div>
      </div>
    );
  }

  return <FindMySchoolDistrictContent districtName={districtName} />;
}

function FindMySchoolDistrictContent({ districtName }: { districtName: string }) {
  const dispatch = useAppDispatch();
  const isMobileViewport = useMobileViewport();

  const [addressInput, setAddressInput] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<SchoolType>("all");
  const [sidebarMode, setSidebarMode] = useState<FindMySchoolSidebarMode>("schools");
  const [highlightedSchoolId, setHighlightedSchoolId] = useState<string | null>(null);
  const [highlightedBoundaryId, setHighlightedBoundaryId] = useState<string | null>(null);
  const [debouncedAddress, setDebouncedAddress] = useState("");
  const [dropPinMode, setDropPinMode] = useState(false);
  const [visibleSuggestions, setVisibleSuggestions] = useState<IGeocodeAddressPublicItem[]>([]);
  const [mobileDrawerSnap, setMobileDrawerSnap] = useState<MobileDrawerSnap>("collapsed");
  const [mobileDrawerMetrics, setMobileDrawerMetrics] =
    useState<MobileDrawerMetrics>(DEFAULT_MOBILE_DRAWER_METRICS);

  const {
    location: userLocation,
    setFromGeocodeResult,
    setFromCoordinates,
    clearLocation,
  } = useUserLocation();

  useEffect(() => {
    dispatch(findMySchoolApi.util.resetApiState());
  }, [dispatch]);

  const { data: districtData, isLoading: isDistrictLoading } =
    useValidateDistrictQuery(districtName, { skip: !districtName });

  const districtBbox = toOptionalDistrictBbox(districtData?.bounds);
  const geocodeQueryArg = useMemo(() => {
    if (!districtBbox) return { address: debouncedAddress };
    const [minLng, minLat, maxLng, maxLat] = districtBbox;
    return { address: debouncedAddress, minLng, minLat, maxLng, maxLat };
  }, [debouncedAddress, districtBbox]);

  const { currentData: geocodeResults, isFetching: isGeocoding } =
    useGeocodeAddressPublicQuery(geocodeQueryArg, {
      skip: !debouncedAddress || debouncedAddress.length < 3,
    });

  useEffect(() => {
    const trimmed = addressInput.trim();
    if (trimmed.length < 3 || debouncedAddress !== trimmed) {
      setVisibleSuggestions([]);
      return;
    }
    setVisibleSuggestions(geocodeResults?.items ?? []);
  }, [addressInput, debouncedAddress, geocodeResults]);

  const showNoAddressResults = shouldShowAddressNoResults({
    addressInput,
    requestedAddress: debouncedAddress,
    isLoading: isGeocoding,
    hasResponse: geocodeResults !== undefined,
    suggestionCount: visibleSuggestions.length,
  });

  const [
    fetchSchools,
    {
      currentData: schoolsData,
      isFetching: isSchoolsLoading,
      originalArgs: schoolsQueryArgs,
    },
  ] = useLazyGetFindMySchoolSchoolsQuery();

  const [
    fetchCommunityAreas,
    {
      currentData: communityData,
      isFetching: isCommunityLoading,
      originalArgs: communityQueryArgs,
    },
  ] = useLazyGetFindMySchoolMapLevelBoundariesQuery();

  const resultsMatchCurrentRequest = schoolResultsMatchRequest(
    schoolsQueryArgs,
    districtData?.districtId ?? null,
    userLocation,
    selectedCategory,
  );
  const communityResultsMatch = communityResultsMatchRequest(
    communityQueryArgs,
    districtData?.districtId ?? null,
    userLocation,
  );
  const searchedAddress = resultsMatchCurrentRequest
    ? (schoolsData?.searchedAddress ?? null)
    : null;
  const hasSearchedAddress =
    userLocation != null &&
    searchedAddress != null &&
    searchedAddress.trim() !== "";
  const hasAddressResults =
    userLocation != null && resultsMatchCurrentRequest && !isSchoolsLoading;
  const isSchoolListLoading = isSchoolsLoading || !resultsMatchCurrentRequest;
  const outsideDistrictBoundary =
    resultsMatchCurrentRequest && Boolean(schoolsData?.outsideDistrictBoundary);
  const schoolItems = resultsMatchCurrentRequest
    ? (schoolsData?.items ?? EMPTY_SCHOOL_ITEMS)
    : EMPTY_SCHOOL_ITEMS;
  const communityItems = communityResultsMatch
    ? (communityData?.items ?? EMPTY_COMMUNITY_ITEMS)
    : EMPTY_COMMUNITY_ITEMS;
  const isCommunityListLoading = isCommunityLoading || !communityResultsMatch;
  const publishedMapLevels = districtData?.publishedMapLevels ?? [];
  const showCommunityMode = publishedMapLevels.length > 0;
  const committedAddress = getCommittedAddress(userLocation, searchedAddress);
  const displayedSchoolItems = useMemo(
    () => getDisplayedSchoolItems(schoolItems, selectedCategory),
    [selectedCategory, schoolItems],
  );
  const highlightedBoundaryBbox = useMemo(
    () => getHighlightedBoundaryBbox(communityItems, highlightedBoundaryId),
    [communityItems, highlightedBoundaryId],
  );

  useEffect(() => {
    if (isMobileViewport == null || sidebarMode !== "schools") return;
    setHighlightedSchoolId((currentSchoolId) => {
      const nextId = resolveNextHighlightedSchoolId({
        isMobileViewport,
        hasAddressResults,
        hasSearchedAddress,
        isSchoolListLoading,
        isSchoolsLoading,
        selectedCategory,
        outsideDistrictBoundary,
        displayedSchoolItems,
        currentSchoolId,
      });
      return nextId === undefined ? currentSchoolId : nextId;
    });
  }, [
    displayedSchoolItems,
    hasAddressResults,
    hasSearchedAddress,
    isMobileViewport,
    isSchoolListLoading,
    isSchoolsLoading,
    outsideDistrictBoundary,
    selectedCategory,
    sidebarMode,
  ]);

  useEffect(() => {
    setMobileDrawerSnap(userLocation ? "middle" : "collapsed");
  }, [userLocation]);

  useEffect(() => {
    if (isMobileViewport) setDropPinMode(false);
  }, [isMobileViewport]);

  useEffect(() => {
    if (selectedCategory === "all") return;
    if (!districtData?.schoolLevelTypes.includes(selectedCategory)) {
      setSelectedCategory("all");
    }
  }, [districtData, selectedCategory]);

  useEffect(() => {
    if (!districtData?.districtId) return;
    fetchSchools(
      buildSchoolsQueryArgs({
        districtId: districtData.districtId,
        selectedCategory,
        userLocation,
      }),
    );
  }, [districtData, userLocation, selectedCategory, fetchSchools]);

  useEffect(() => {
    if (!districtData?.districtId || !showCommunityMode) return;
    fetchCommunityAreas(
      buildCommunityQueryArgs({
        districtId: districtData.districtId,
        userLocation,
      }),
    );
  }, [districtData, userLocation, showCommunityMode, fetchCommunityAreas]);

  useEffect(() => {
    if (showCommunityMode) return;
    setSidebarMode("schools");
    setHighlightedBoundaryId(null);
  }, [showCommunityMode]);

  useEffect(() => {
    if (!highlightedBoundaryId || isCommunityListLoading) return;
    const stillVisible = communityItems.some(
      (item) => item.boundaryId === highlightedBoundaryId,
    );
    if (!stillVisible) setHighlightedBoundaryId(null);
  }, [communityItems, highlightedBoundaryId, isCommunityListLoading]);

  useEffect(() => {
    const trimmed = addressInput.trim();
    if (trimmed.length < 3) {
      setDebouncedAddress("");
      return;
    }
    const timer = setTimeout(() => setDebouncedAddress(trimmed), 300);
    return () => clearTimeout(timer);
  }, [addressInput]);

  useEffect(() => {
    if (userLocation && resultsMatchCurrentRequest && searchedAddress) {
      setAddressInput(searchedAddress);
    }
  }, [resultsMatchCurrentRequest, searchedAddress, userLocation]);

  const handleAddressSearch = useCallback((address: string) => {
    if (address.length >= 3) setDebouncedAddress(address);
  }, []);

  const handleAddressSelect = useCallback(
    (item: IGeocodeAddressPublicItem) => {
      setFromGeocodeResult(item);
      setAddressInput(item.formattedAddress ?? "");
      setDebouncedAddress("");
      setVisibleSuggestions([]);
      setHighlightedSchoolId(null);
      setHighlightedBoundaryId(null);
      setMobileDrawerSnap("middle");
    },
    [setFromGeocodeResult],
  );

  const handleMapClick = useCallback(
    (lat: number, lng: number) => {
      if (!dropPinMode) return;
      setFromCoordinates(lat, lng);
      setAddressInput("");
      setDebouncedAddress("");
      setVisibleSuggestions([]);
      setDropPinMode(false);
      setHighlightedSchoolId(null);
      setHighlightedBoundaryId(null);
      setMobileDrawerSnap("middle");
    },
    [dropPinMode, setFromCoordinates],
  );

  const handleDropPinComplete = useCallback(() => setDropPinMode(false), []);

  const handleMobileDropPinClick = useCallback(() => {
    if (!dropPinMode) setMobileDrawerSnap("collapsed");
    setDropPinMode(!dropPinMode);
  }, [dropPinMode]);

  const handleMapSchoolClick = useCallback(
    (school: IFindMySchoolSchoolItem) => {
      setSidebarMode("schools");
      setHighlightedBoundaryId(null);
      setHighlightedSchoolId(school.id);
      if (isMobileViewport) setMobileDrawerSnap("middle");
    },
    [isMobileViewport],
  );

  const handleSchoolCardClick = useCallback(
    (school: IFindMySchoolSchoolItem) => {
      setHighlightedBoundaryId(null);
      setHighlightedSchoolId((currentSchoolId) => {
        if (isMobileViewport && currentSchoolId === school.id) return null;
        return school.id;
      });
    },
    [isMobileViewport],
  );

  const handleCommunityItemClick = useCallback(
    (item: IFindMySchoolMapLevelBoundaryItem) => {
      setHighlightedSchoolId(null);
      setHighlightedBoundaryId((currentId) =>
        currentId === item.boundaryId ? null : item.boundaryId,
      );
      if (isMobileViewport) setMobileDrawerSnap("middle");
    },
    [isMobileViewport],
  );

  const handleSidebarModeChange = useCallback((mode: FindMySchoolSidebarMode) => {
    setSidebarMode(mode);
    if (mode === "schools") setHighlightedBoundaryId(null);
    else setHighlightedSchoolId(null);
  }, []);

  const handleCategoryChange = useCallback(
    (category: SchoolType) => {
      setSelectedCategory(category);
      if (isMobileViewport) setHighlightedSchoolId(null);
    },
    [isMobileViewport],
  );

  const handleClearAddress = useCallback(() => {
    setAddressInput("");
    setDebouncedAddress("");
    clearLocation();
    setDropPinMode(false);
    setHighlightedSchoolId(null);
    setHighlightedBoundaryId(null);
    setVisibleSuggestions([]);
    setMobileDrawerSnap("collapsed");
  }, [clearLocation]);

  const handleAddressInputChange = useCallback(
    (value: string) => {
      if (value === "") {
        handleClearAddress();
        return;
      }
      setAddressInput(value);
    },
    [handleClearAddress],
  );

  const handleMobileDrawerMetricsChange = useCallback((metrics: MobileDrawerMetrics) => {
    setMobileDrawerMetrics((currentMetrics) => {
      const unchanged =
        currentMetrics.height === metrics.height &&
        currentMetrics.controlsOpacity === metrics.controlsOpacity &&
        currentMetrics.mapBottomPadding === metrics.mapBottomPadding;
      return unchanged ? currentMetrics : metrics;
    });
  }, []);

  if (isDistrictLoading || isMobileViewport == null) {
    return <LoadingScreen />;
  }

  if (!districtData) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">
            District Not Found
          </h1>
          <p className="mt-2 text-gray-600">
            The district "{districtName}" was not found. Please check the
            district name and try again.
          </p>
        </div>
      </div>
    );
  }

  const districtCenter = getDistrictCenter(districtData);
  const sharedLayoutProps = {
    districtData,
    addressInput,
    dropPinMode,
    isGeocoding,
    showNoAddressResults,
    visibleSuggestions,
    onAddressInputChange: handleAddressInputChange,
    onClearAddress: handleClearAddress,
    onAddressSearch: handleAddressSearch,
    onAddressSelect: handleAddressSelect,
  };

  const mapContent = (
    <MapStyleProvider>
      <MapProvider>
        <FindMySchoolLayersProvider
          hasDistrictLayer
          districtSchoolLevelTypes={districtData.schoolLevelTypes ?? []}
          publishedMapLevels={districtData.publishedMapLevels ?? []}
        >
          <FindMySchoolMap
            districtBounds={districtData.bounds ?? null}
            districtCenter={districtCenter}
            districtId={districtData.districtId}
            dropPinMode={dropPinMode}
            highlightedBoundaryBbox={highlightedBoundaryBbox}
            highlightedBoundaryId={highlightedBoundaryId}
            highlightedSchoolId={highlightedSchoolId}
            mobile={isMobileViewport}
            mobileControlsOpacity={mobileDrawerMetrics.controlsOpacity}
            mobileDrawerHeight={mobileDrawerMetrics.height}
            mobileMapBottomPadding={mobileDrawerMetrics.mapBottomPadding}
            schools={displayedSchoolItems}
            selectedCategory={selectedCategory}
            userLocation={userLocation}
            onDropPinComplete={handleDropPinComplete}
            onMapClick={handleMapClick}
            onSchoolClick={handleMapSchoolClick}
          />
        </FindMySchoolLayersProvider>
      </MapProvider>
    </MapStyleProvider>
  );

  if (isMobileViewport) {
    return (
      <FindMySchoolMobileLayout
        {...sharedLayoutProps}
        committedAddress={committedAddress}
        communityItems={communityItems}
        displayedSchoolItems={displayedSchoolItems}
        hasAddress={userLocation != null}
        hasAddressResults={hasAddressResults}
        highlightedBoundaryId={highlightedBoundaryId}
        highlightedSchoolId={highlightedSchoolId}
        isCommunityListLoading={isCommunityListLoading}
        isSchoolListLoading={isSchoolListLoading}
        mapContent={mapContent}
        mobileDrawerSnap={mobileDrawerSnap}
        outsideDistrictBoundary={outsideDistrictBoundary}
        selectedCategory={selectedCategory}
        showCommunityMode={showCommunityMode}
        sidebarMode={sidebarMode}
        onCategoryChange={handleCategoryChange}
        onCommunityItemClick={handleCommunityItemClick}
        onMobileDrawerMetricsChange={handleMobileDrawerMetricsChange}
        onMobileDrawerSnapChange={setMobileDrawerSnap}
        onMobileDropPinClick={handleMobileDropPinClick}
        onSchoolCardClick={handleSchoolCardClick}
        onSidebarModeChange={handleSidebarModeChange}
      />
    );
  }

  return (
    <FindMySchoolDesktopLayout
      {...sharedLayoutProps}
      communityItems={communityItems}
      displayedSchoolItems={displayedSchoolItems}
      hasSearchedAddress={hasSearchedAddress}
      hasUserLocation={userLocation != null}
      highlightedBoundaryId={highlightedBoundaryId}
      highlightedSchoolId={highlightedSchoolId}
      isCommunityListLoading={isCommunityListLoading}
      isSchoolsLoading={isSchoolsLoading}
      mapContent={mapContent}
      outsideDistrictBoundary={outsideDistrictBoundary}
      searchedAddress={searchedAddress}
      selectedCategory={selectedCategory}
      showCommunityMode={showCommunityMode}
      sidebarMode={sidebarMode}
      onCategoryChange={handleCategoryChange}
      onCommunityItemClick={handleCommunityItemClick}
      onDropPinClick={() => setDropPinMode(true)}
      onSchoolCardClick={handleSchoolCardClick}
      onSidebarModeChange={handleSidebarModeChange}
    />
  );
}

export default function FindMySchoolPage() {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <FindMySchoolContent />
    </Suspense>
  );
}
